"use client"

import type React from "react"

import { useState, useRef, useEffect } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Upload, Camera, Loader2, MapPin, Wrench, ZoomIn, ChevronLeft, ChevronRight, Square } from "lucide-react"
import NextImage from "next/image"
import { WizardStepper } from "./WizardStepper"
import { CanvasConfirmation } from "./CanvasConfirmation"
import { StyleSelector } from "./StyleSelector"
import RoomColorSelections from "@/app/components/RoomColorSelections"
import { GeneratedDesigns } from "./GeneratedDesigns"
import { GenerationLoadingComponent } from "@/app/components/GenerationLoadingComponent"
import { ImageCropper } from "@/app/components/image-cropper"
import { ImageGenerationAspectSelector } from "@/app/components/ImageGenerationAspectSelector"
import { getUISessionManager, updateUISession } from "@/src/utils/ui-session-manager"
import { SaveCanvasModal } from "@/app/components/SaveCanvasModal"
import {
  startCanvasGenerationWizard,
  appendGenerationImages,
  saveCanvas,
  startCanvasConceptWizard,
  saveProject,
} from "@/src/utils/userDataApi"
import { useSession } from "@/app/context/AuthContext"
import { generateRoomConceptsFromCanvas } from "@/src/utils/aiAggregatorApi"
// import { analyzeRoomProperties } from "@/src/utils/aiAggregatorApi"
import { generateRoomCanvas } from "@/src/utils/aiAggregatorApi"
import { BillingErrorComponent } from "@/app/components/BillingErrorComponent"
import { isBillingError } from "@/src/utils/billingApi"
import { useBillableActions } from "@/app/context/BillableActionsContext"
import { useWizardStatePreservation, type BaseWizardState } from "@/hooks/useWizardStatePreservation"
import { toast } from "sonner"

import { ROOM_TYPE_MAPPINGS, ROOM_THEME_MAPPINGS } from "@/src/utils/api-value-mappings"

import {
  Sofa,
  BedDouble,
  ChefHat,
  Bath,
  Briefcase,
  Utensils,
  BedSingle,
  Flower as Shower,
  Baby,
  Gamepad2,
  Users,
  DoorOpen,
  Coffee,
  Wine,
  UtensilsCrossed,
  Shirt,
  Heart,
  Armchair,
} from "lucide-react"

import { convertImageIfNeeded, validateImageFile, type ConversionProgress } from "@/src/utils/image-converter"
import { ImageConversionProgress } from "@/app/components/image-conversion-progress"
import { ImageModal } from "@/app/components/ui/ImageModal"
import { BillableActionInfo } from "@/app/components/BillableActionInfo"
// import { ImageGenerationAspectSelector } from "@/app/components/ImageGenerationAspectSelector" // Duplicate import removed

// Sort by order and create icon mapping
const sortedRoomTypes = [...ROOM_TYPE_MAPPINGS].sort((a, b) => a.order - b.order)

const iconMap: { [key: string]: any } = {
  Sofa,
  BedDouble,
  ChefHat,
  Bath,
  Briefcase,
  Utensils,
  BedSingle,
  Shower,
  Baby,
  Gamepad2,
  Users,
  DoorOpen,
  Coffee,
  Wine,
  UtensilsCrossed,
  Shirt,
  Heart,
  Armchair,
}

const ROOM_SIZES = [
  { value: "extra-small", label: "Extra Small", description: "Less than 100 sq ft" },
  { value: "small", label: "Small", description: "100 - 200 sq ft" },
  { value: "medium", label: "Medium", description: "200 - 300 sq ft" },
  { value: "large", label: "Large", description: "300 - 400 sq ft" },
  { value: "extra-large", label: "Extra Large", description: "More than 400 sq ft" },
]

const FIXED_ELEMENTS = {
  structural: [
    "Windows",
    "Doors",
    "French Doors",
    "Sliding Doors",
    "Fireplace",
    "Built-in Shelving",
    "Built-in Cabinets",
    "Columns",
    "Beams",
    "Archways",
    "Stairs",
    "Railings",
  ],
  kitchenBathroom: [
    "Kitchen Island",
    "Peninsula",
    "Breakfast Bar",
    "Built-in Appliances",
    "Range Hood",
    "Vanity",
    "Bathtub",
    "Shower",
    "Toilet",
  ],
  builtInFeatures: [
    "Entertainment Center",
    "Desk/Workspace",
    "Window Seat",
    "Bay Window",
    "Nook",
    "Closet",
    "Walk-in Closet",
    "Pantry",
  ],
  architectural: [
    "Crown Molding",
    "Wainscoting",
    "Chair Rail",
    "Ceiling Fan",
    "Recessed Lighting",
    "HVAC Vents",
    "Radiators",
  ],
}

// Ensure we always have the shape RoomDetailsConfirmation expects
function normalizeRoomDetails(details: any) {
  return {
    // copy everything else first
    ...details,
    // fallback values
    roomType: details?.roomType ?? "living",
    dimensions: {
      width: details?.dimensions?.width ?? 12,
      length: details?.dimensions?.length ?? 12,
    },
    fixedElements: Array.isArray(details?.fixedElements) ? details.fixedElements : [],
  }
}

const dataURLtoFile = (dataurl: string, filename: string): File => {
  const arr = dataurl.split(",")
  const mime = arr[0].match(/:(.*?);/)?.[1] || "image/jpeg"
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}

interface UploadRoomWizardState extends BaseWizardState {
  wizardType: "upload-room"
  currentStep: number
  colorSubStep: number
  uploadedImage: string | null
  apiSessionId: string | null
  canvasCreationWizardId: string | null
  savedCanvasId: string | null
  canvasConceptWizardId: string | null
  apiImageUrl: string | null
  apiCanvasImageUrls: string[]
  apiDesignImageUrls: string[]
  roomDetails: {
    roomType: string
    dimensions: {
      width: number
      length: number
    }
    fixedElements: string[]
  }
  manualRoomDetails: {
    roomType: string
    fixedElements: string[]
  }
  selectedCanvas: string | null
  savedCanvasName: string
  selectedStyle: string
  selectedPalette: string
  selectedPrimaryColor: string
  colorDetails: string
  colorPreferences: { preferHues: string[]; avoidHues: string[] }
  selectedMood: "calm" | "balanced" | "bold"
  selectedIntensity: "muted" | "medium" | "vibrant"
  selectedAccentWall: boolean
  canvasImages: string[]
  generatedDesigns: string[]
  uiSessionId: string | null
  currentRoomTypePage: number
  landscape: boolean | null
  canvasOrientation: boolean | null // Added this property
}

export function UploadRoomWizard() {
  const router = useRouter()
  const { data: session, refreshCredits } = useSession() // Added refreshCredits
  const { getActionCost } = useBillableActions()

  // State variables declared here to be used in useWizardStatePreservation
  const [currentStep, setCurrentStep] = useState(1)
  const [colorSubStep, setColorSubStep] = useState(1)
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [showImageUploader, setShowImageUploader] = useState(false)
  const [showCropper, setShowCropper] = useState(false)
  const [isProcessingImage, setIsProcessingImage] = useState(false)
  const [roomDetails, setRoomDetails] = useState<any>(null)
  const [manualRoomDetails, setManualRoomDetails] = useState({
    roomType: "",
    fixedElements: [],
  })
  const [roomTypeError, setRoomTypeError] = useState<string | null>(null)
  const [canvasImages, setCanvasImages] = useState<string[]>([])
  const [selectedCanvas, setSelectedCanvas] = useState<string | null>(null)
  const [savedCanvasName, setSavedCanvasName] = useState<string>("")
  const [selectedStyle, setSelectedStyle] = useState<string>("")
  const [selectedPalette, setSelectedPalette] = useState<string>("")
  const [selectedPrimaryColor, setSelectedPrimaryColor] = useState<string>("")
  const [generatedDesigns, setGeneratedDesigns] = useState<string[]>([])
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [isGeneratingCanvas, setIsGeneratingCanvas] = useState(false)
  const [isRegeneratingCanvas, setIsRegeneratingCanvas] = useState(false)
  const [isGeneratingDesigns, setIsGeneratingDesigns] = useState(false)
  const [isRegeneratingDesigns, setIsRegeneratingDesigns] = useState(false)
  const [showSaveCanvasModal, setShowSaveCanvasModal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [colorError, setColorError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedDesign, setSelectedDesign] = useState<string | null>(null)
  const [colorDetails, setColorDetails] = useState<string>("")

  // Session management state
  const [uiSessionId, setUISessionId] = useState<string | null>(null)
  const [apiSessionId, setApiSessionId] = useState<string | null>(null)
  const [apiImageUrl, setApiImageUrl] = useState<string | null>(null)
  const [canvasCreationWizardId, setCanvasCreationWizardId] = useState<string | null>(null)
  const [savedCanvasId, setSavedCanvasId] = useState<string | null>(null)
  const [canvasConceptWizardId, setCanvasConceptWizardId] = useState<string | null>(null)

  // API URL tracking state
  const [apiCanvasImageUrls, setApiCanvasImageUrls] = useState<string[]>([])
  const [apiDesignImageUrls, setApiDesignImageUrls] = useState<string[]>([])

  const [isSavingCanvas, setIsSavingCanvas] = useState(false)
  const [saveCanvasError, setSaveCanvasError] = useState<string | null>(null)

  const [selectedImageForModal, setSelectedImageForModal] = useState<string | null>(null)
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)

  const [selectedMood, setSelectedMood] = useState<"calm" | "balanced" | "bold">("balanced")
  const [selectedIntensity, setSelectedIntensity] = useState<"muted" | "medium" | "vibrant">("medium")
  const [selectedAccentWall, setSelectedAccentWall] = useState(false)

  const [colorPreferences, setColorPreferences] = useState<{ preferHues: string[]; avoidHues: string[] }>({
    preferHues: [],
    avoidHues: [],
  })

  const [currentRoomTypePage, setCurrentRoomTypePage] = useState(0)
  const roomTypesPerPage = 6
  const totalRoomTypePages = Math.ceil(sortedRoomTypes.length / roomTypesPerPage)

  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false)

  const [isConverting, setIsConverting] = useState(false)
  const [conversionProgress, setConversionProgress] = useState<ConversionProgress | null>(null)
  const [needsCropping, setNeedsCropping] = useState(false) // Added state for cropping decision

  const [landscape, setLandscape] = useState<boolean | null>(null)
  // CHANGE: Added separate state for canvas orientation to preserve it through the wizard
  const [canvasOrientation, setCanvasOrientation] = useState<boolean | null>(null)

  const hasRestoredRef = useRef(false) // Not strictly needed with the new hook, but kept for now

  const sessionManager = getUISessionManager() // Declare sessionManager here

  const { isRestoring, onBeforePurchase, clearSavedState, restoredState } =
    useWizardStatePreservation<UploadRoomWizardState>({
      wizardId: "upload-room",
      state: {
        wizardType: "upload-room",
        timestamp: Date.now(),
        userId: session?.user?.id || "",
        currentStep: currentStep,
        colorSubStep: colorSubStep,
        uploadedImage: uploadedImage,
        apiSessionId: apiSessionId,
        canvasCreationWizardId: canvasCreationWizardId,
        savedCanvasId: savedCanvasId,
        canvasConceptWizardId: canvasConceptWizardId,
        apiImageUrl: apiImageUrl,
        apiCanvasImageUrls: apiCanvasImageUrls,
        apiDesignImageUrls: apiDesignImageUrls,
        roomDetails: roomDetails,
        manualRoomDetails: manualRoomDetails,
        selectedCanvas: selectedCanvas,
        savedCanvasName: savedCanvasName,
        selectedStyle: selectedStyle,
        selectedPalette: selectedPalette,
        selectedPrimaryColor: selectedPrimaryColor,
        colorDetails: colorDetails,
        colorPreferences: colorPreferences,
        selectedMood: selectedMood,
        selectedIntensity: selectedIntensity,
        selectedAccentWall: selectedAccentWall,
        canvasImages: canvasImages,
        generatedDesigns: generatedDesigns,
        uiSessionId: uiSessionId,
        currentRoomTypePage: currentRoomTypePage,
        landscape: landscape,
        canvasOrientation: canvasOrientation, // Added this to state
      },
      validateState: (state) => {
        if (state.userId !== session?.user?.id) {
          console.warn("[v0] upload-room: User ID mismatch, rejecting restoration")
          return false
        }
        return true
      },
      onRestore: (restored) => {
        console.log("[v0] Restoring UploadRoom wizard state:", restored)

        setCurrentStep(restored.currentStep)
        setColorSubStep(restored.colorSubStep)
        setApiSessionId(restored.apiSessionId)
        setCanvasCreationWizardId(restored.canvasCreationWizardId)
        setSavedCanvasId(restored.savedCanvasId)
        setCanvasConceptWizardId(restored.canvasConceptWizardId)
        setApiImageUrl(restored.apiImageUrl)
        setApiCanvasImageUrls(restored.apiCanvasImageUrls)
        setApiDesignImageUrls(restored.apiDesignImageUrls)
        setRoomDetails(restored.roomDetails)
        setManualRoomDetails(restored.manualRoomDetails)
        setSelectedCanvas(restored.selectedCanvas)
        setSavedCanvasName(restored.savedCanvasName)
        setSelectedStyle(restored.selectedStyle)
        setSelectedPalette(restored.selectedPalette)
        setSelectedPrimaryColor(restored.selectedPrimaryColor)
        setColorDetails(restored.colorDetails)
        setColorPreferences(restored.colorPreferences)
        setSelectedMood(restored.selectedMood)
        setSelectedIntensity(restored.selectedIntensity)
        setSelectedAccentWall(restored.selectedAccentWall)
        setCanvasImages(restored.canvasImages)
        setGeneratedDesigns(restored.generatedDesigns)
        setCurrentRoomTypePage(restored.currentRoomTypePage)
        setLandscape(restored.landscape ?? null)
        setCanvasOrientation(restored.canvasOrientation ?? null) // Restored canvas orientation

        if (restored.uploadedImage) {
          console.log("[v0] Restored image from saved state")
          setUploadedImage(restored.uploadedImage)

          const recreatedFile = dataURLtoFile(restored.uploadedImage, "restored-room-image.png")
          setUploadedFile(recreatedFile)
        } else {
          console.warn("[v0] No image found in saved state")
        }

        if (restored.uiSessionId) {
          setUISessionId(restored.uiSessionId)
        }

        toast.success("Credits added! Continuing where you left off...")
      },
    })

  // Wizard step labels (titles only)
  const steps = [
    "Upload Room Photo",
    "Confirm Room Details",
    "Select Canvas",
    "Choose Style",
    "Colors Selected",
    "Generate Designs",
  ]

  const getRoomTypeLabel = (roomType?: string) => {
    if (!roomType) return "Unknown Room"
    const roomTypeObj = sortedRoomTypes.find((room) => room.value === roomType)
    return roomTypeObj ? roomTypeObj.name : roomType.charAt(0).toUpperCase() + roomType.slice(1)
  }

  const selections = [
    uploadedImage ? "Room Uploaded" : null,
    manualRoomDetails.roomType ? getRoomTypeLabel(manualRoomDetails.roomType) : null,
    selectedCanvas ? "Canvas Selected" : null,
    selectedStyle ? ROOM_THEME_MAPPINGS.find((theme) => theme.value === selectedStyle)?.name || selectedStyle : null,
    selectedPalette || selectedPrimaryColor ? "Colors Selected" : null,
    generatedDesigns.length > 0 ? "Generated Designs" : null,
  ]

  // Check if image needs cropping based on aspect ratio
  const checkImageAspectRatio = (width: number, height: number): boolean => {
    try {
      const aspectRatio = width / height
      const tolerance = 0.05

      // Check for 3:2 aspect ratio (1.5)
      const is3to2 = Math.abs(aspectRatio - 1.5) <= tolerance

      // Check for 1:1 aspect ratio (1.0)
      const is1to1 = Math.abs(aspectRatio - 1.0) <= tolerance

      const result = is3to2 || is1to1
      return result
    } catch (error) {
      console.error("🔧 DEBUG: ERROR in checkImageAspectRatio:", error)
      return false
    }
  }

  const getCurrentPageRoomTypes = () => {
    const startIndex = currentRoomTypePage * roomTypesPerPage
    return sortedRoomTypes.slice(startIndex, startIndex + roomTypesPerPage)
  }

  // Modified to return needsCropping
  const checkImageProperties = async (file: File): Promise<{ needsCropping: boolean }> => {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        const imageConforms = checkImageAspectRatio(img.width, img.height)
        const needsCropping = !imageConforms // Invert the logic
        resolve({ needsCropping })
      }
      img.onerror = () => {
        console.error("Failed to load image for property check.")
        resolve({ needsCropping: false }) // Default to no cropping if image fails to load
      }
      img.src = URL.createObjectURL(file)
    })
  }

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    console.log("[v0] Starting image upload process for file:", file.name, "size:", file.size)
    setIsProcessingImage(true)
    setError(null)

    try {
      // Step 1: Validate file
      console.log("[v0] Step 1: Validating file")
      const validation = validateImageFile(file)
      if (!validation.isValid) {
        console.log("[v0] File validation failed:", validation.error)
        setError(validation.error || "Invalid file")
        setIsProcessingImage(false)
        return
      }
      console.log("[v0] File validation passed")

      // Step 2: Convert if needed (HEIC to PNG)
      console.log("[v0] Step 2: Starting conversion process")
      setIsConverting(true)

      const conversionResult = await convertImageIfNeeded(file, (progress) => {
        console.log("[v0] Conversion progress:", progress.message, `${progress.progress}%`)
        setConversionProgress(progress)
      })

      if (!conversionResult.success) {
        console.log("[v0] Conversion failed:", conversionResult.error)
        const errorMessage = conversionResult.error || "File conversion failed"
        if (
          errorMessage.includes("exceeds maximum") ||
          errorMessage.includes("timeout") ||
          errorMessage.includes("too large")
        ) {
          setError(`${errorMessage}. Please try a smaller image file (under 15MB).`)
        } else {
          setError(errorMessage)
        }
        setIsProcessingImage(false)
        setIsConverting(false)
        return
      }
      console.log("[v0] Conversion completed successfully")

      const processedFile = conversionResult.file!
      setUploadedFile(processedFile)
      setIsConverting(false)
      setConversionProgress(null)

      // Step 3: Read file and continue with existing flow
      console.log("[v0] Step 3: Reading file and checking properties")
      const reader = new FileReader()
      reader.onload = async (e) => {
        const imageDataUrl = e.target?.result as string
        console.log("[v0] File read successfully, setting uploaded image")
        setUploadedImage(imageDataUrl)

        console.log("[v0] Checking if image needs cropping")
        const { needsCropping } = await checkImageProperties(processedFile)
        console.log("[v0] Cropping check result - needsCropping:", needsCropping)
        setNeedsCropping(needsCropping)

        setIsProcessingImage(false)

        if (needsCropping) {
          console.log("[v0] Image needs cropping, showing cropper automatically")
          setShowCropper(true)
        } else {
          console.log("[v0] Image doesn't need cropping, proceeding to manual input")
          proceedToManualInput()
        }
      }
      reader.readAsDataURL(processedFile)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to process image"
      console.log("[v0] Error in image upload process:", errorMessage)
      if (errorMessage.includes("memory") || errorMessage.includes("timeout") || errorMessage.includes("too large")) {
        setError(`${errorMessage}. Please try a smaller image file (under 15MB).`)
      } else {
        setError(errorMessage)
      }
      setIsProcessingImage(false)
      setIsConverting(false)
      setConversionProgress(null)
    }
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleCroppedImage = (croppedImageUrl: string) => {
    setUploadedImage(croppedImageUrl)
    setShowCropper(false)
    const croppedFile = dataURLtoFile(croppedImageUrl, "cropped-room-image.jpg")
    setUploadedFile(croppedFile)
    proceedToManualInput()
  }

  const handleImageSaved = (savedImageUrl: string) => {
    // Handle the saved image - this is called after cropping is complete
    setUploadedImage(savedImageUrl)
    const savedFile = dataURLtoFile(savedImageUrl, "saved-room-image.jpg")
    setUploadedFile(savedFile)
  }

  const handleCropperBack = () => {
    // Handle going back from the cropper
    setShowCropper(false)
    setUploadedImage(null)
    setUploadedFile(null)
    setCurrentStep(1)
  }

  const proceedToManualInput = () => {
    console.log("[v0] proceedToManualInput called")
    setIsProcessingImage(false)

    // Ensure we have a file object
    if (uploadedImage && !uploadedFile) {
      console.log("[v0] Creating file object from uploaded image")
      const file = dataURLtoFile(uploadedImage, "room-image.jpg")
      setUploadedFile(file)
    }

    // Create UI session
    const sessionManager = getUISessionManager()
    const newUISessionId = sessionManager.startSession("flow-a")
    console.log("[v0] Created UI session:", newUISessionId)
    setUISessionId(newUISessionId)

    // Store in session manager
    updateUISession(newUISessionId, {
      originalImage: uploadedImage,
      step: 2,
    })

    // Move to step 2 for manual input
    console.log("[v0] Moving to step 2")
    setCurrentStep(2)
  }

  const cleanErrorMessage = (errorMessage: string): string => {
    return errorMessage.replace(/^Network error occurred:\s*/i, "")
  }

  /**
   * Flow A – Step 2
   * The user has confirmed / edited the detected room details.
   * We now ask the AI to generate two blank-canvas images, store them, append
   * them to the canvas-generation wizard, and advance to the “Select Canvas”
   * step.
   */
  const handleRoomDetailsConfirm = async (confirmedDetails?: any) => {
    const detailsToUse = confirmedDetails || {
      roomType: manualRoomDetails.roomType,
      dimensions: { width: 12, length: 15 }, // Default dimensions
      fixedElements: manualRoomDetails.fixedElements,
    }

    if (!detailsToUse.roomType) {
      setRoomTypeError("Please select a room type before proceeding.")
      window.scrollTo({ top: 0, behavior: "smooth" })
      return
    }

    if (landscape === null) {
      setRoomTypeError("Please select a canvas orientation before proceeding.")
      window.scrollTo({ top: 0, behavior: "smooth" })
      return
    }

    console.log("✅ Room details confirmed, generating canvas") // dev log;
    setRoomDetails(normalizeRoomDetails(detailsToUse))
    setIsGeneratingCanvas(true)
    setError(null)
    setRoomTypeError(null)

    const handleGenerateCanvas = async () => {
      try {
        const result = await generateRoomCanvas({
          userId: session?.user?.id,
          imageFile: uploadedFile, // Use file instead of imageUrl
          roomType: detailsToUse.roomType,
          fixedElements: detailsToUse.fixedElements,
          landscape: landscape,
        })

        if (result.success === false) {
          setError(result.error || "Canvas generation failed")
          return
        }

        const wizardResp = await startCanvasGenerationWizard({
          userId: session?.user?.id || "",
          userSessionId: uiSessionId || "",
          // Cannot store raw image data.  Future versions will have URL
          sourceImageUrl: "RAW IMAGE NOT ALLOWED",
          analysisJson: result,
          sessionId: result.sessionid,
        })

        if (!wizardResp.success || !wizardResp.wizardId) {
          throw new Error(wizardResp.error || "wizard start failed")
        }

        // Store wizard and session info
        const currentWizardId = wizardResp.wizardId
        setCanvasCreationWizardId(currentWizardId)
        setApiSessionId(result.sessionid)
        setApiImageUrl(result.imageUrl || "")

        /*───────────────── 2. Pick images or fall back ───────────────────*/
        const imgs = result.imageUrls || [
          "/images/canvas-options/empty-room-canvas.png",
          "/images/canvas-options/room-with-rug-canvas.png",
        ]
        setCanvasImages(imgs)
        setApiCanvasImageUrls(imgs)

        /*───────────────── 3. Persist in session store ───────────────────*/
        const sessionManager = getUISessionManager()
        if (uiSessionId) {
          updateUISession(uiSessionId, {
            roomDetails: detailsToUse,
            canvasImages: imgs,
            step: 3,
          })
        }

        /*───────────────── 4. Append to wizard row (fire-and-forget) ─────*/
        if (currentWizardId) {
          appendGenerationImages({
            wizardId: currentWizardId,
            type: "canvas-generation",
            newImageUrls: imgs,
            increment: imgs.length,
          }).catch((err) => console.error("Wizard append failed:", err))
        }

        setCanvasOrientation(landscape)

        /*───────────────── 5. Advance UI step ────────────────────────────*/
        console.log("➡️ Moving to step 3: Select Canvas")
        setCurrentStep(3)

        await refreshCredits()
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Canvas generation failed"
        const cleanedError = cleanErrorMessage(errorMessage)

        if (isBillingError(cleanedError)) {
          // Suppress console error for billing issues, handle gracefully in UI
          setError(cleanedError)
          window.scrollTo({ top: 0, behavior: "smooth" })
        } else {
          setError(cleanedError)
          window.scrollTo({ top: 0, behavior: "smooth" })
        }
      } finally {
        setIsGeneratingCanvas(false)
      }
    }

    await handleGenerateCanvas()
  }

  const handleCanvasSelect = (canvasId: string) => {
    setSelectedCanvas(canvasId)
  }

  /**
   * Regenerate the blank-canvas images after the user leaves feedback
   * (“add rug”, “remove sofa”, etc.).  Uses the central
   * `generateRoomCanvas` helper so we no longer touch `fetch` directly.
   */
  const handleCanvasRegenerate = async () => {
    console.log("[v0] handleCanvasRegenerate called - scrolling to top")
    window.scrollTo({ top: 0, behavior: "instant" })

    console.log("🔄 Regenerating canvas")
    setIsRegeneratingCanvas(true)
    setError(null)

    try {
      const result = await generateRoomCanvas({
        userId: session?.user?.id,
        imageFile: uploadedFile, // Use file instead of imageUrl
        roomType: roomDetails.roomType,
        fixedElements: roomDetails.fixedElements,
        landscape: landscape, // Pass landscape here too
      })

      if (result.success === false) {
        setError(result.error || "Canvas regeneration failed")
        return
      }

      const imgs = result.imageUrls || [
        "/images/canvas-options/empty-room-canvas.png",
        "/images/canvas-options/room-with-rug-canvas.png",
      ]
      setCanvasImages(imgs)
      setApiCanvasImageUrls(imgs)

      // Append to wizard
      if (canvasCreationWizardId) {
        appendGenerationImages({
          wizardId: canvasCreationWizardId,
          type: "canvas-generation",
          newImageUrls: imgs,
          increment: imgs.length,
        }).catch((err) => console.error("Wizard append failed:", err))
      }

      await refreshCredits()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Canvas regeneration failed"
      const cleanedError = cleanErrorMessage(errorMessage)

      if (isBillingError(cleanedError)) {
        // Suppress console error for billing issues, handle gracefully in UI
        setError(cleanedError)
      } else {
        setError(cleanedError)
      }
    } finally {
      setIsRegeneratingCanvas(false)
    }
  }

  const handleCanvasNext = () => {
    if (!selectedCanvas) {
      setError("Please select a canvas option to continue.")
      return
    }

    // Show save canvas modal
    console.log("🏠 UploadRoomWizard: Opening save canvas modal")
    setShowSaveCanvasModal(true)
  }

  const handleSaveCanvas = async (canvasName: string) => {
    console.log("💾 Saving canvas:", canvasName)

    setIsSavingCanvas(true)
    setSaveCanvasError(null)

    try {
      // Validation - ensure all required fields are present
      const userId = session?.user?.id
      if (!userId) {
        throw new Error("User not authenticated")
      }

      if (!canvasCreationWizardId) {
        throw new Error("Canvas creation wizard session not found")
      }

      if (!uploadedImage) {
        throw new Error("Source image not found")
      }

      if (!apiSessionId) {
        throw new Error("API session not found")
      }

      if (!selectedCanvas) {
        throw new Error("No canvas selected")
      }

      // Get selected canvas image URL from API URLs
      const canvasIndex = Number.parseInt(selectedCanvas.split("-")[1] || "0")
      const selectedCanvasImageUrl = apiCanvasImageUrls[canvasIndex]

      if (!selectedCanvasImageUrl) {
        throw new Error("Selected canvas image not found")
      }

      const fixedElementsString =
        manualRoomDetails.fixedElements && manualRoomDetails.fixedElements.length > 0
          ? manualRoomDetails.fixedElements.join(",")
          : "none"

      // Prepare save request
      const saveRequest = {
        userId,
        wizardId: canvasCreationWizardId,
        name: canvasName || "Untitled Canvas",
        // Cannot store raw image data.  Future versions will have URL
        sourceImageUrl: "RAW IMAGE NOT ALLOWED",
        roomType: manualRoomDetails.roomType || "living-room",
        fixedElements: fixedElementsString,
        sessionId: apiSessionId,
        canvasImageUrl: selectedCanvasImageUrl, // Use API response URL
        landscape: landscape === true ? true : false, // Include landscape parameter
      }

      // Call the API
      const response = await saveCanvas(saveRequest)

      if (response.success) {
        console.log("✅ Canvas saved successfully:", response.canvasId)

        // Store the canvas ID for later use
        setSavedCanvasId(response.canvasId)

        // Update local state
        setSavedCanvasName(canvasName)

        // Update session with saved canvas
        const sessionManager = getUISessionManager()
        if (uiSessionId) {
          updateUISession(uiSessionId, {
            savedCanvasName: canvasName,
            selectedCanvas,
            step: 4, // Moving to step 4
          })
        }

        // Clear canvas creation wizard session - it's complete
        setCanvasCreationWizardId(null)
        console.log("✅ Canvas creation wizard session completed")

        setShowSaveCanvasModal(false)
        console.log("➡️ Moving to step 4: Choose Style")
        setCurrentStep(4) // Move to step 4: Choose Style
      } else {
        throw new Error(response.error || "Failed to save canvas")
      }
    } catch (error) {
      console.error("❌ Error saving canvas:", error)
      setSaveCanvasError(error instanceof Error ? error.message : "Failed to save canvas")
    } finally {
      setIsSavingCanvas(false)
    }
  }

  const handleStyleSelect = (styleValue: string) => {
    setSelectedStyle(styleValue)
  }

  const handleStyleNext = () => {
    if (!selectedStyle) {
      setError("Please select a style to continue.")
      return
    }

    setLandscape(null)

    console.log("➡️ Moving to step 5: Color Preferences")
    setCurrentStep(5)
    setColorSubStep(1)
  }

  const handleColorDetailsChange = (
    colorDetails: string,
    preferHues: string[],
    avoidHues: string[],
    mood: string,
    intensity: string,
    accentWall: boolean,
  ) => {
    console.log("[v0] 🎨 Color details changed:", { mood, intensity, accentWall, preferHues, avoidHues })
    setColorDetails(colorDetails)
    setColorPreferences({ preferHues, avoidHues })
    setSelectedMood(mood as "calm" | "balanced" | "bold") // Type assertion
    setSelectedIntensity(intensity as "muted" | "medium" | "vibrant") // Type assertion
    setSelectedAccentWall(accentWall)
    setColorError(null)
  }

  const handleColorSubStepChange = (step: number) => {
    setColorSubStep(step)
  }

  const handleColorNext = async () => {
    if (colorSubStep === 1 && colorPreferences.preferHues.length === 0) {
      setColorError("Please choose at least one color for your room")
      window.scrollTo({ top: 0, behavior: "smooth" })
      return
    }

    if (colorSubStep < 3) {
      // Move to next color sub-step
      setColorSubStep(colorSubStep + 1)
      window.scrollTo({ top: 0, behavior: "smooth" })
      return
    }

    // Final step - generate designs
    if (!colorDetails) {
      setError("Please complete the color selection to continue.")
      return
    }

    if (landscape === null) {
      setColorError("Please select an orientation before generating designs.")
      window.scrollTo({ top: 0, behavior: "smooth" })
      return
    }

    console.log("✅ All selections complete, generating designs")
    console.log("🎨 Color Details:", colorDetails)
    //console.log("Fixed Elements:", fixedElements)
    setIsGeneratingDesigns(true)
    setError(null)

    const handleGenerateDesigns = async () => {
      window.scrollTo({ top: 0, behavior: "smooth" })

      try {
        if (!apiSessionId) {
          throw new Error("No API session ID available")
        }

        console.log("🎯 Step 6: Generating designs with API session:", apiSessionId)

        const canvasIndex: number = Number.parseInt(selectedCanvas?.split("-")[1] || "0")
        const selectedCanvasImageUrl: string = apiCanvasImageUrls[canvasIndex]

        const apiResult = await generateRoomConceptsFromCanvas({
          userId: session?.user?.id,
          sessionId: apiSessionId,
          imageUrl: selectedCanvasImageUrl,
          roomType: roomDetails.roomType,
          roomTheme: selectedStyle,
          colorDetails: colorDetails,
          fixedElements: roomDetails.fixedElements,
          regeneration: false,
          landscape: landscape,
        })

        if (apiResult.success === false) {
          setError(apiResult.error || "Failed to generate designs")
          setGeneratedDesigns([])
          setApiDesignImageUrls([])
          // Remove setCurrentStep(6) to stay on step 5
          return
        }

        if (apiResult.designs) {
          setGeneratedDesigns(apiResult.designs)
          setApiDesignImageUrls(apiResult.designs)

          try {
            const wizardResponse = await startCanvasConceptWizard({
              userId: session?.user?.id || "",
              userSessionId: uiSessionId || "",
              sessionId: apiSessionId,
              canvasId: savedCanvasId || "",
              roomTheme: selectedStyle,
              colorDetails: colorDetails,
              fixedElements: roomDetails.fixedElements?.join(", ") || "",
              imageUrls: apiResult.designs,
            })
            if (wizardResponse.success && wizardResponse.wizardId) {
              setCanvasConceptWizardId(wizardResponse.wizardId)
              console.log("✅ Canvas concept wizard started:", wizardResponse.wizardId)
            } else {
              setError("Failed to start canvas concept wizard")
            }
          } catch (err) {
            setError("Error starting canvas concept wizard")
          }

          console.log("➡️ Moving to step 6: Generated Designs")
          setCurrentStep(6)
        } else {
          setError("Failed to generate designs. Using demo designs.")
          setGeneratedDesigns([])
          setApiDesignImageUrls([])
          setCurrentStep(6)
        }

        await refreshCredits()
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to generate designs. Using demo designs."
        const cleanedError = cleanErrorMessage(errorMessage)

        if (isBillingError(cleanedError)) {
          // Suppress console error for billing issues, handle gracefully in UI
          setError(cleanedError)
        } else {
          setError(cleanedError)
          setGeneratedDesigns([])
          setApiDesignImageUrls([])
          setCurrentStep(6)
        }
      } finally {
        setIsGeneratingDesigns(false)
      }
    }

    await handleGenerateDesigns()
  }

  const handleBack = () => {
    // Don't allow back navigation during processing states
    if (
      isProcessingImage ||
      isAnalyzing ||
      isConverting ||
      isGeneratingCanvas ||
      isGeneratingDesigns ||
      isRegeneratingCanvas ||
      isRegeneratingDesigns
    ) {
      return
    }

    if (currentStep > 1) {
      console.log(`⬅️ Going back from step ${currentStep} to step ${currentStep - 1}`)
      if (currentStep === 2) {
        setIsProcessingImage(false)
        setIsAnalyzingImage(false)
      }
      setCurrentStep(currentStep - 1)
    }
  }

  // Updated handleComplete to use clearSavedState and endSession
  const handleComplete = async () => {
    try {
      const projectId = `project-${Date.now()}`

      console.log("💾 Saving project:", {
        projectId,
        selectedDesign,
        canvasName: savedCanvasName,
        roomDetails,
        style: selectedStyle,
        palette: selectedPalette,
        primaryColor: selectedPrimaryColor,
        designs: generatedDesigns,
      })

      clearSavedState()
      if (uiSessionId) {
        sessionManager.endSession(uiSessionId)
      }

      router.push(`/dashboard/projects/${projectId}`)
    } catch (error) {
      console.error("Error creating project:", error)
    }
  }

  const handleCancel = async () => {
    console.log("❌ Wizard cancelled")

    // Clean up session on cancel
    const sessionManager = getUISessionManager()
    if (uiSessionId) {
      console.log("🧹 Ending UI session:", uiSessionId)
      sessionManager.endSession(uiSessionId)
    }

    // Clear saved state on cancel as well
    clearSavedState()

    // Force navigation to designer page
    console.log("🔄 Navigating to /designer")

    // Use window.location for immediate redirect
    if (typeof window !== "undefined") {
      window.location.href = "/designer"
    } else {
      // Fallback for server-side
      router.push("/designer")
    }
  }

  const getFixedElementsLabel = (elements?: string[]) => {
    if (!elements || elements.length === 0) return "None specified"
    return elements
      .map((element) => {
        switch (element) {
          case "windows":
            return "Windows"
          case "doors":
            return "Doors"
          case "built_ins":
            return "Built-ins"
          case "electrical":
            return "Electrical"
          case "vents":
            return "Vents"
          case "fireplace":
            return "Fireplace"
          case "cabinets":
            return "Cabinets"
          case "countertops":
            return "Countertops"
          case "appliances":
            return "Appliances"
          default:
            return element.charAt(0).toUpperCase() + element.slice(1)
        }
      })
      .join(", ")
  }

  const getSelectedCanvasImage = () => {
    if (!selectedCanvas || !canvasImages.length) return ""
    const canvasIndex = Number.parseInt(selectedCanvas.split("-")[1]) || 0
    return canvasImages[canvasIndex] || canvasImages[0]
  }

  const handleImageClick = (imageUrl: string) => {
    setSelectedImageForModal(imageUrl)
    setIsImageModalOpen(true)
  }

  const closeImageModal = () => {
    setIsImageModalOpen(false)
    setSelectedImageForModal(null)
  }

  // CHANGE: Add useEffect to scroll to top when colorSubStep changes
  useEffect(() => {
    // Scroll to top whenever colorSubStep changes
    if (currentStep === 5) {
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }, [colorSubStep, currentStep])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [currentStep])

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isImageModalOpen) {
        closeImageModal()
      }
    }

    if (isImageModalOpen) {
      document.addEventListener("keydown", handleEscKey)
      document.body.style.overflow = "hidden"
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey)
      document.body.style.overflow = "unset"
    }
  }, [isImageModalOpen])

  // Canvas Details Panel Component
  const CanvasDetailsPanel = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      {/* Canvas Preview */}
      <Card>
        <CardHeader>
          <CardTitle>{savedCanvasName || "Your Canvas"}</CardTitle>
          <CardDescription>Clean room canvas ready for design generation</CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className="relative aspect-video rounded-lg overflow-hidden bg-gray-100 group cursor-pointer"
            onClick={() => handleImageClick(getSelectedCanvasImage() || "/placeholder.svg")}
          >
            <NextImage
              src={getSelectedCanvasImage() || "/placeholder.svg"}
              alt="Selected canvas"
              fill
              className="object-cover transition-transform duration-200 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
              <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Room Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Room Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* CHANGE: Use canvasOrientation instead of landscape to show canvas orientation */}
          {currentStep >= 4 && canvasOrientation !== null && (
            <div>
              <label className="text-sm font-medium text-gray-500">Orientation</label>
              <div className="mt-1 flex items-center gap-2">
                <Square className="h-4 w-4 text-gray-400" />
                <span className="text-sm">{canvasOrientation ? "Landscape" : "Square"}</span>
              </div>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-gray-500">Room Type</label>
            <div className="mt-1">
              <Badge variant="secondary">{getRoomTypeLabel(roomDetails?.roomType)}</Badge>
            </div>
          </div>

          {currentStep >= 5 && selectedStyle && (
            <div>
              <label className="text-sm font-medium text-gray-500">Selected Style</label>
              <div className="mt-1">
                <Badge variant="secondary">
                  {ROOM_THEME_MAPPINGS.find((theme) => theme.value === selectedStyle)?.name || selectedStyle}
                </Badge>
              </div>
            </div>
          )}

          {currentStep >= 6 && colorDetails && (
            <div>
              <label className="text-sm font-medium text-gray-500">Color Details</label>
              <div className="mt-1">
                <Badge variant="secondary">{colorDetails}</Badge>
              </div>
            </div>
          )}

          {/* Display Mood, Intensity, and Accent Wall */}
          {currentStep >= 5 && (
            <>
              <div>
                <label className="text-sm font-medium text-gray-500">Mood</label>
                <div className="mt-1">
                  <Badge variant="secondary">{selectedMood.charAt(0).toUpperCase() + selectedMood.slice(1)}</Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Intensity</label>
                <div className="mt-1">
                  <Badge variant="secondary">
                    {selectedIntensity.charAt(0).toUpperCase() + selectedIntensity.slice(1)}
                  </Badge>
                </div>
              </div>
              {selectedAccentWall && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Accent Wall</label>
                  <div className="mt-1">
                    <Badge variant="secondary">Included</Badge>
                  </div>
                </div>
              )}
            </>
          )}

          <div>
            <label className="text-sm font-medium text-gray-500">Fixed Elements</label>
            <div className="mt-1 flex items-start gap-2">
              <Wrench className="h-4 w-4 text-gray-400 mt-0.5" />
              <span className="text-sm">{getFixedElementsLabel(roomDetails?.fixedElements)}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )

  // Show cropper if image needs cropping
  if (showCropper && uploadedImage) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <ImageCropper imageSrc={uploadedImage} onCroppedImage={handleCroppedImage} onImageSaved={handleImageSaved} />
      </div>
    )
  }

  // Show processing state
  if (isProcessingImage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <h3 className="text-lg font-semibold">Processing your image...</h3>
        <p className="text-gray-600 text-center max-w-md">
          Checking image properties and determining if cropping is needed.
        </p>
      </div>
    )
  }

  if (isConverting && conversionProgress) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <ImageConversionProgress progress={conversionProgress} fileName={uploadedFile?.name} />
      </div>
    )
  }

  // Show analyzing state
  if (isAnalyzing) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <h3 className="text-lg font-semibold">Analyzing your room...</h3>
        <p className="text-gray-600 text-center max-w-md">
          Our AI is examining your photo to identify room type, style, dimensions, and color palette.
        </p>
      </div>
    )
  }

  // Show canvas generation state
  if (isGeneratingCanvas) {
    const canvasGenerationTime =
      landscape === true
        ? Number(process.env.NEXT_PUBLIC_GENERATE_CANVASES_LANDSCAPE) || 95000
        : Number(process.env.NEXT_PUBLIC_GENERATE_CANVASES_PORTRAIT) || 70000

    return (
      <GenerationLoadingComponent
        message="Generating canvas..."
        showText={true}
        estimatedDuration={canvasGenerationTime}
      />
    )
  }

  // Show design generation state
  if (isGeneratingDesigns) {
    return (
      <GenerationLoadingComponent
        message="Generating your designs..."
        showText={true}
        estimatedDuration={Number(process.env.NEXT_PUBLIC_GENERATE_CONCEPTS_FROM_CANVAS_PORTRAIT)}
      />
    )
  }

  // -- Generated-Design handlers -------------------
  const handleDesignSelect = (designId: string) => {
    setSelectedDesign(designId)
  }

  /**
   * Regenerate additional design concepts using the same canvas + parameters.
   * Utilises the shared aiAggregatorApi helper to keep API plumbing in one place.
   */
  const handleDesignRegenerate = async (newDesigns: string[]) => {
    window.scrollTo({ top: 0, behavior: "smooth" })

    setError(null)
    setIsRegeneratingDesigns(true)

    try {
      if (!apiSessionId) {
        throw new Error("No API session ID available")
      }

      const canvasIndex: number = Number.parseInt(selectedCanvas?.split("-")[1] || "0")
      const selectedCanvasImageUrl: string = apiCanvasImageUrls[canvasIndex]

      const apiResult = await generateRoomConceptsFromCanvas({
        userId: session?.user?.id,
        sessionId: apiSessionId,
        imageUrl: selectedCanvasImageUrl,
        roomType: roomDetails.roomType,
        roomTheme: selectedStyle,
        colorDetails: colorDetails,
        fixedElements: roomDetails.fixedElements,
        regeneration: true,
        landscape: landscape,
      })

      if (apiResult.success === false) {
        setError(apiResult.error || "Failed to regenerate designs")
        return
      }

      if (apiResult.designs) {
        /* Append new designs to current state */
        setGeneratedDesigns((prev) => [...prev, ...apiResult.designs!])
        setApiDesignImageUrls((prev) => [...prev, ...apiResult.designs!])

        if (!canvasConceptWizardId) {
          try {
            const wizardResponse = await startCanvasConceptWizard({
              userId: session?.user?.id || "",
              userSessionId: uiSessionId || "",
              sessionId: apiSessionId,
              canvasId: savedCanvasId || "",
              roomTheme: selectedStyle,
              colorDetails: colorDetails,
              fixedElements: roomDetails.fixedElements?.join(", ") || "",
              imageUrls: apiResult.designs!,
            })
            if (wizardResponse.success && wizardResponse.wizardId) {
              setCanvasConceptWizardId(wizardResponse.wizardId)
              console.log("✅ Canvas concept wizard created during regeneration:", wizardResponse.wizardId)
            } else {
              setError("Failed to create canvas concept wizard during regeneration")
            }
          } catch (err) {
            setError("Error creating canvas concept wizard during regeneration")
          }
        } else {
          /* Append to existing canvas-concept wizard session */
          try {
            await appendGenerationImages({
              wizardId: canvasConceptWizardId,
              type: "canvas-concept",
              newImageUrls: apiResult.designs!,
              increment: apiResult.designs!.length,
            })
            console.log("✅ Canvas concept wizard updated with regenerated designs")
          } catch (err) {
            setError("Failed to update canvas concept wizard")
          }
        }
      } else {
        setError("Failed to regenerate designs")
      }

      await refreshCredits()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to regenerate designs"
      const cleanedError = cleanErrorMessage(errorMessage)

      if (isBillingError(cleanedError)) {
        // Suppress console error for billing issues, handle gracefully in UI
        setError(cleanedError)
      } else {
        setError(cleanedError)
      }
    } finally {
      setIsRegeneratingDesigns(false)
    }
  }

  const handleCompleteFinal = async () => {
    let selectedDesignUrl = null

    // Get the actual selected design URL from API URLs
    if (typeof selectedDesign === "string") {
      if (selectedDesign.startsWith("https")) {
        /* 👉 handle the https URL case */
        console.log("selectedDesign is the URL:", selectedDesign)
        selectedDesignUrl = selectedDesign
      } else if (selectedDesign.startsWith("design")) {
        // FIX: The original code was missing the logic to parse selectedDesign if it's a string starting with "design"
        const selectedDesignIndex = selectedDesign ? Number.parseInt(selectedDesign.split("-")[1] || "0") : 0
        selectedDesignUrl = apiDesignImageUrls[selectedDesignIndex]
      } else if (selectedDesign.startsWith("/images")) {
        console.log("selectedDesign is the URL:", selectedDesign)
        selectedDesignUrl = selectedDesign
      }
    } else if (typeof selectedDesign === "number" || /^\d+$/.test(selectedDesign)) {
      selectedDesignUrl = apiDesignImageUrls[selectedDesign as number] // Ensure type assertion for numeric index
    } else {
      // Fallback or error handling for unexpected selectedDesign types
      console.error("❌ Unexpected type for selectedDesign:", typeof selectedDesign, selectedDesign)
      setError("Failed to determine selected design URL.")
      return
    }

    if (selectedDesignUrl === null) {
      console.error("❌ Failed to save canvas project as the selected image url cannot be determined.")
      console.error(`selectedDesign is ${selectedDesign}`)
      console.error(`apiDesignImageUrls is ${JSON.stringify(apiDesignImageUrls)}`)
      setError("Failed to save project. Please try again.")
      return // Prevent proceeding if URL is not determined
    }

    // Save canvas project
    const saveResponse = await saveProject({
      kind: "canvas",
      data: {
        userId: session?.user?.id || "",
        wizardId: canvasConceptWizardId || "",
        canvasId: savedCanvasId,
        name: `${savedCanvasName} Project`,
        sessionId: apiSessionId || "",
        roomTheme: selectedStyle,
        roomDimension: roomDetails.roomSize || "medium",
        colorDetails: colorDetails,
        fixedElements: Array.isArray(roomDetails?.fixedElements)
          ? roomDetails.fixedElements.join(", ")
          : roomDetails?.fixedElements || "",
        projectImageUrl: selectedDesignUrl, // Use the determined URL
        canvasImageUrl: apiCanvasImageUrls[Number.parseInt(selectedCanvas?.split("-")[1] || "0")] || "", // Use API response URL
        landscape: landscape === true,
      },
    })

    if (saveResponse.success) {
      console.log("✅ Canvas project saved successfully:", saveResponse.projectId)

      clearSavedState()
      if (uiSessionId) {
        sessionManager.endSession(uiSessionId)
      }

      router.push(`/dashboard/projects/${saveResponse.projectId}?kind=canvas`)
    } else {
      console.error("❌ Failed to save canvas project:", saveResponse.error)
      setError("Failed to save project. Please try again.")
    }
  }

  console.log("🏠 UploadRoomWizard: Rendering with showSaveCanvasModal:", showSaveCanvasModal)

  const analyzeRoomCost = getActionCost("ANALYZE_ROOM")
  const canvasGenerationCost = getActionCost("GENERATE_CANVASES")
  const canvasGenerationLandscapeCost = getActionCost("GENERATE_CANVASES_LANDSCAPE")
  const conceptGenerationCost = getActionCost("GENERATE_CONCEPTS_FROM_CANVAS")
  const conceptGenerationLandscapeCost = getActionCost("GENERATE_CONCEPTS_FROM_CANVAS_LANDSCAPE")

  if (isRestoring) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Restoring your progress...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <WizardStepper steps={steps} currentStep={currentStep} selections={selections} />

        {/* CHANGE: Replaced standard error display with BillingErrorComponent */}
        {error && isBillingError(error) && (
          <BillingErrorComponent errorMessage={error} onBeforePurchase={onBeforePurchase} />
        )}

        {error && !isBillingError(error) && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800">{error}</p>
          </div>
        )}

        {saveCanvasError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{saveCanvasError}</p>
          </div>
        )}

        {/* STEP 1: Upload Room Photo */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">Upload Your Room Photo</h2>
              <p className="text-gray-600">
                Take a photo or upload an existing image of the room you'd like to redesign
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={handleUploadClick}>
                <CardContent className="flex flex-col items-center justify-center p-8 text-center">
                  <Upload className="h-12 w-12 text-blue-600 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Upload Photo</h3>
                  <p className="text-gray-600 text-sm">Choose a photo from your device</p>
                </CardContent>
              </Card>

              <Card className="cursor-pointer hover:shadow-lg transition-shadow opacity-50">
                <CardContent className="flex flex-col items-center justify-center p-8 text-center">
                  <Camera className="h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold mb-2 text-gray-400">Take Photo</h3>
                  <p className="text-gray-500 text-sm">Coming soon</p>
                </CardContent>
              </Card>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>
        )}

        {/* STEP 2: Confirm Room Details */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">Confirm Room Details</h2>
              <p className="text-gray-600">Please provide details about your room</p>
            </div>

            {uploadedImage && (
              <div className="flex justify-center">
                <img
                  src={uploadedImage || "/placeholder.svg"}
                  alt="Uploaded room"
                  className="w-full max-w-md mx-auto rounded-lg shadow-lg"
                />
              </div>
            )}

            {roomTypeError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{roomTypeError}</p>
              </div>
            )}

            {/* Room Type Selection */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Room Type</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {getCurrentPageRoomTypes().map((type) => {
                  const IconComponent = iconMap[type.icon]
                  return (
                    <button
                      key={type.value}
                      onClick={() => {
                        setManualRoomDetails((prev) => ({ ...prev, roomType: type.value }))
                        setRoomTypeError(null)
                      }}
                      className={`p-4 rounded-lg border text-left transition-colors flex items-center gap-3 ${
                        manualRoomDetails.roomType === type.value
                          ? "border-[#40A1AB] bg-[#40A1AB]/10 text-[#40A1AB]"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {IconComponent && <IconComponent className="w-5 h-5 flex-shrink-0" />}
                      <span className="font-medium">{type.name}</span>
                    </button>
                  )
                })}
              </div>

              {totalRoomTypePages > 1 && (
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setCurrentRoomTypePage(Math.max(0, currentRoomTypePage - 1))}
                    disabled={currentRoomTypePage === 0}
                    className="flex items-center gap-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>

                  <div className="flex gap-2">
                    {Array.from({ length: totalRoomTypePages }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentRoomTypePage(i)}
                        className={`w-8 h-8 rounded-full text-sm ${
                          currentRoomTypePage === i
                            ? "bg-[#40A1AB] text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => setCurrentRoomTypePage(Math.min(totalRoomTypePages - 1, currentRoomTypePage + 1))}
                    disabled={currentRoomTypePage === totalRoomTypePages - 1}
                    className="flex items-center gap-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              <p className="text-sm text-gray-500">
                If your room type is not shown, please use our suggestion form to request it be added
              </p>
            </div>

            {/* Fixed Elements Selection */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold">Fixed Elements</h3>
                <p className="text-sm text-gray-500">Select any fixed elements in your room</p>
              </div>

              {/* Structural Elements */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-700">Structural</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {FIXED_ELEMENTS.structural.map((element) => (
                    <button
                      key={element}
                      onClick={() => {
                        setManualRoomDetails((prev) => ({
                          ...prev,
                          fixedElements: prev.fixedElements.includes(element)
                            ? prev.fixedElements.filter((e) => e !== element)
                            : [...prev.fixedElements, element],
                        }))
                      }}
                      className={`p-2 rounded-md border text-sm transition-colors ${
                        manualRoomDetails.fixedElements.includes(element)
                          ? "border-[#40A1AB] bg-[#40A1AB]/10 text-[#40A1AB]"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {element}
                    </button>
                  ))}
                </div>
              </div>

              {/* Kitchen/Bathroom Elements */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-700">Kitchen/Bathroom</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {FIXED_ELEMENTS.kitchenBathroom.map((element) => (
                    <button
                      key={element}
                      onClick={() => {
                        setManualRoomDetails((prev) => ({
                          ...prev,
                          fixedElements: prev.fixedElements.includes(element)
                            ? prev.fixedElements.filter((e) => e !== element)
                            : [...prev.fixedElements, element],
                        }))
                      }}
                      className={`p-2 rounded-md border text-sm transition-colors ${
                        manualRoomDetails.fixedElements.includes(element)
                          ? "border-[#40A1AB] bg-[#40A1AB]/10 text-[#40A1AB]"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {element}
                    </button>
                  ))}
                </div>
              </div>

              {/* Built-in Features */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-700">Built-in Features</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {FIXED_ELEMENTS.builtInFeatures.map((element) => (
                    <button
                      key={element}
                      onClick={() => {
                        setManualRoomDetails((prev) => ({
                          ...prev,
                          fixedElements: prev.fixedElements.includes(element)
                            ? prev.fixedElements.filter((e) => e !== element)
                            : [...prev.fixedElements, element],
                        }))
                      }}
                      className={`p-2 rounded-md border text-sm transition-colors ${
                        manualRoomDetails.fixedElements.includes(element)
                          ? "border-[#40A1AB] bg-[#40A1AB]/10 text-[#40A1AB]"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {element}
                    </button>
                  ))}
                </div>
              </div>

              {/* Architectural Details */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-700">Architectural</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {FIXED_ELEMENTS.architectural.map((element) => (
                    <button
                      key={element}
                      onClick={() => {
                        setManualRoomDetails((prev) => ({
                          ...prev,
                          fixedElements: prev.fixedElements.includes(element)
                            ? prev.fixedElements.filter((e) => e !== element)
                            : [...prev.fixedElements, element],
                        }))
                      }}
                      className={`p-2 rounded-md border text-sm transition-colors ${
                        manualRoomDetails.fixedElements.includes(element)
                          ? "border-[#40A1AB] bg-[#40A1AB]/10 text-[#40A1AB]"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {element}
                    </button>
                  ))}
                </div>
              </div>

              {manualRoomDetails.fixedElements.length > 0 && (
                <p className="text-sm text-gray-600">
                  {manualRoomDetails.fixedElements.length} element
                  {manualRoomDetails.fixedElements.length !== 1 ? "s" : ""} selected
                </p>
              )}
            </div>

            {uploadedImage && (
              <ImageGenerationAspectSelector
                imageUrl={uploadedImage}
                landscape={landscape}
                onLandscapeChange={setLandscape}
                squareCredits={canvasGenerationCost}
                landscapeCredits={canvasGenerationLandscapeCost}
              />
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={handleBack}>
                Back
              </Button>
              <div className="flex flex-col items-end gap-2">
                <Button
                  className="bg-[#40A1AB] hover:bg-[#3A919A] text-white"
                  onClick={() => {
                    handleRoomDetailsConfirm()
                  }}
                  disabled={isGeneratingCanvas || landscape === null}
                >
                  {isGeneratingCanvas ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating Canvas...
                    </>
                  ) : (
                    <>
                      {(() => {
                        if (landscape === null) {
                          return "Generate Canvas"
                        }
                        const cost = landscape === true ? canvasGenerationLandscapeCost : canvasGenerationCost
                        console.log(
                          "[v0] Generate Canvas button - landscape:",
                          landscape,
                          "cost:",
                          cost,
                          "landscapeCost:",
                          canvasGenerationLandscapeCost,
                          "squareCost:",
                          canvasGenerationCost,
                        )
                        return `Generate Canvas${cost > 0 ? ` (${cost} credits)` : ""}`
                      })()}
                    </>
                  )}
                </Button>
                <BillableActionInfo />
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: Select Canvas */}
        {currentStep === 3 && canvasImages.length > 0 && (
          <div className="space-y-6">
            <CanvasConfirmation
              canvasImages={canvasImages}
              selectedCanvas={selectedCanvas}
              onSelect={handleCanvasSelect}
              onRegenerate={handleCanvasRegenerate}
              isRegenerating={isRegeneratingCanvas}
              sessionId={apiSessionId || undefined}
              wizardId={canvasCreationWizardId || undefined}
              userId={session?.user?.id}
              userName={session?.user?.name}
              userEmail={session?.user?.email}
              landscape={landscape} // Pass landscape to CanvasConfirmation
              estimatedDuration={
                landscape === true
                  ? Number(process.env.NEXT_PUBLIC_GENERATE_CANVASES_LANDSCAPE) || 95000
                  : Number(process.env.NEXT_PUBLIC_GENERATE_CANVASES_PORTRAIT) || 70000
              }
            />

            <div className="flex justify-between">
              <Button variant="outline" onClick={handleBack}>
                Back
              </Button>
              <Button
                className="bg-[#40A1AB] hover:bg-[#3A919A] text-white"
                onClick={handleCanvasNext}
                disabled={!selectedCanvas}
              >
                Next: Save Canvas
              </Button>
            </div>
          </div>
        )}

        {/* STEP 4: Choose Style */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <CanvasDetailsPanel />
            <StyleSelector selectedStyle={selectedStyle} onSelect={handleStyleSelect} />

            <div className="flex justify-between">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                className="bg-[#40A1AB] hover:bg-[#3A919A] text-white"
                onClick={handleStyleNext}
                disabled={!selectedStyle}
              >
                Next: Color Preferences
              </Button>
            </div>
          </div>
        )}

        {/* STEP 5: Color Preferences */}
        {currentStep === 5 && (
          <div className="space-y-6">
            {colorError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{colorError}</p>
              </div>
            )}

            <CanvasDetailsPanel />
            <RoomColorSelections
              onColorDetailsChange={handleColorDetailsChange}
              currentStep={colorSubStep}
              onStepChange={setColorSubStep}
              selectedPalette={selectedPalette}
              setSelectedPalette={setSelectedPalette}
              selectedPrimaryColor={selectedPrimaryColor}
              setSelectedPrimaryColor={setSelectedPrimaryColor}
              initialMood={selectedMood}
              initialIntensity={selectedIntensity}
              initialAccentWall={selectedAccentWall}
              initialPreferHues={colorPreferences.preferHues}
              initialAvoidHues={colorPreferences.avoidHues}
            />

            {colorSubStep === 3 && selectedCanvas && (
              <ImageGenerationAspectSelector
                imageUrl={apiCanvasImageUrls[Number.parseInt(selectedCanvas.split("-")[1]) || 0]}
                landscape={landscape}
                onLandscapeChange={setLandscape}
                squareCredits={conceptGenerationCost}
                landscapeCredits={conceptGenerationLandscapeCost}
                title="Design Orientation"
                subtitle="Choose how your designs will be generated"
              />
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={handleBack}>
                Back
              </Button>
              <div className="flex flex-col items-end gap-2">
                <Button
                  className="bg-[#40A1AB] hover:bg-[#3A919A] text-white"
                  onClick={handleColorNext}
                  disabled={isGeneratingDesigns || (colorSubStep === 3 && landscape === null)}
                >
                  {isGeneratingDesigns ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : colorSubStep < 3 ? (
                    "Next"
                  ) : (
                    <>
                      Generate Designs
                      {landscape !== null &&
                        (landscape
                          ? conceptGenerationLandscapeCost > 0 && ` (${conceptGenerationLandscapeCost} credits)`
                          : conceptGenerationCost > 0 && ` (${conceptGenerationCost} credits)`)}
                    </>
                  )}
                </Button>
                {colorSubStep === 3 && (
                  <div>
                    <BillableActionInfo />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* STEP 6: Generated Designs */}
        {currentStep === 6 && (
          <GeneratedDesigns
            designs={generatedDesigns}
            selectedDesign={selectedDesign}
            onSelect={handleDesignSelect}
            isGenerating={false}
            isRegenerating={isRegeneratingDesigns}
            onRegenerate={handleDesignRegenerate}
            onComplete={handleCompleteFinal}
            onBack={handleBack}
            imageSet="canvas"
            landscape={landscape}
            estimatedDuration={
              landscape
                ? Number(process.env.NEXT_PUBLIC_GENERATE_CONCEPTS_FROM_CANVAS_LANDSCAPE) || 105000
                : Number(process.env.NEXT_PUBLIC_GENERATE_CONCEPTS_FROM_CANVAS_PORTRAIT)
            }
          />
        )}

        {/* Save Canvas Modal */}
        {showSaveCanvasModal && (
          <SaveCanvasModal
            isOpen={showSaveCanvasModal}
            onClose={() => setShowSaveCanvasModal(false)}
            onSave={handleSaveCanvas}
            canvasImage={selectedCanvas ? canvasImages[Number.parseInt(selectedCanvas.split("-")[1]) || 0] : ""}
            isLoading={isSavingCanvas}
          />
        )}

        {isImageModalOpen && selectedImageForModal && (
          <ImageModal
            imageSrc={selectedImageForModal}
            caption={savedCanvasName || "Your Canvas"}
            isOpen={isImageModalOpen}
            onClose={closeImageModal}
            showDownload={false}
          />
        )}
      </div>
    </div>
  )
}

export default UploadRoomWizard
